import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import { Refresh as RefreshIcon, Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon, Visibility as VisibilityIcon, Block as BlockIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import awsConfig from '../../config/aws-config';
import { awsUserService } from '../../services/aws-user-service';
import { awsAnalyticsService } from '../../services/aws-analytics-service';
import { awsAuditService, AuditLogEntry } from '../../services/aws-audit-service';
import { ROLES, ROLE_LABELS, SUPPORT_ROLES } from '../../types/roles';
import { isAdminEmail } from '../../config/admin';
import AnimatedLoader from '../../components/common/AnimatedLoader';
import { useNavigate, useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';
import TestingTools from '../TestingTools';

const SuperAdminDashboard: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<any[]>([]);
    const [openInviteDialog, setOpenInviteDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any | null>(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState(ROLES.SUPPORT_L1);
    const [editRole, setEditRole] = useState('');
    const [editStatus, setEditStatus] = useState('active');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [tabValue, setTabValue] = useState(0);

    const [stats, setStats] = useState({
        users: 0,
        invoices: 0,
        clients: 0,
        inventory: 0,
        transactions: 0
    });

    const [logs, setLogs] = useState<AuditLogEntry[]>([]);

    useEffect(() => {
        // strict check for super admin
        if (!currentUser || !isAdminEmail(currentUser.email)) {
            navigate('/dashboard');
            return;
        }

        // Handle URL Query Params for Tabs
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam) {
            const tabIndex = parseInt(tabParam, 10);
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
                setTabValue(tabIndex);
            }
        }

        fetchUsers();
        fetchStats();
        fetchLogs();
    }, [currentUser, navigate, location.search]);

    const fetchLogs = async () => {
        const recentLogs = await awsAuditService.getRecentLogs();
        setLogs(recentLogs);
    };

    const fetchStats = async () => {
        try {
            const [
                invoicesCount,
                clientsCount,
                inventoryCount,
                transactionsCount
            ] = await Promise.all([
                awsAnalyticsService.getTotalInvoices(),
                awsAnalyticsService.getTotalClients(),
                awsAnalyticsService.getTotalInventory(),
                awsAnalyticsService.getTotalTransactions()
            ]);

            setStats(prev => ({
                ...prev,
                invoices: invoicesCount,
                clients: clientsCount,
                inventory: inventoryCount,
                transactions: transactionsCount
            }));
        } catch (e) {
            console.error('Error fetching stats:', e);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const allItems = await awsUserService.listAllUsers();
            // Filter out regular users if the list is huge, but for now show all that are relevant
            // We want to separate 'invites' from 'active users'
            setUsers(allItems);
            setStats(prev => ({ ...prev, users: allItems.length }));
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch user list');
        } finally {
            setLoading(false);
        }
    };

    const handleInviteSubmit = async () => {
        if (!inviteEmail) {
            setError('Email is required');
            return;
        }

        try {
            setLoading(true);
            await awsUserService.createSupportInvite(inviteEmail, inviteRole, currentUser?.email || 'admin');
            await awsAuditService.logAction('INVITE_CREATED', currentUser?.email || 'admin', `Invited ${inviteEmail} as ${inviteRole}`, inviteEmail);
            setSuccess(`Invite sent to ${inviteEmail}`);
            setOpenInviteDialog(false);
            setInviteEmail('');
            fetchUsers();
            fetchLogs();
        } catch (err: any) {
            setError('Failed to create invite: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteInvite = async (email: string) => {
        if (window.confirm(`Are you sure you want to delete the invite for ${email}?`)) {
            try {
                await awsUserService.deleteInvite(email);
                await awsAuditService.logAction('INVITE_DELETED', currentUser?.email || 'admin', `Deleted invite for ${email}`, email);
                fetchUsers();
                setSuccess('Invite deleted');
                fetchLogs();
            } catch (err) {
                setError('Failed to delete invite');
            }
        }
    };

    const handleViewUser = (user: any) => {
        setSelectedUser(user);
        setOpenViewDialog(true);
    };

    const handleEditUser = (user: any) => {
        setSelectedUser(user);
        setEditRole(user.role);
        setEditStatus(user.status || 'active');
        setOpenEditDialog(true);
    };

    const handleEditSubmit = async () => {
        if (!selectedUser) return;

        try {
            setLoading(true);
            await awsUserService.updateUserProfile(selectedUser.userId || selectedUser.uid, {
                role: editRole,
                status: editStatus
            });
            await awsAuditService.logAction('USER_UPDATED', currentUser?.email || 'admin', `Updated role to ${editRole} and status to ${editStatus}`, selectedUser.email, selectedUser.userId);
            setSuccess(`User ${selectedUser.email} updated`);
            setOpenEditDialog(false);
            fetchUsers();
            fetchLogs();
        } catch (err: any) {
            setError('Failed to update user: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const activeSupportUsers = users.filter(u => u.type !== 'support_invite' && SUPPORT_ROLES.includes(u.role));
    const pendingInvites = users.filter(u => u.type === 'support_invite');
    const allOtherUsers = users.filter(u => u.type !== 'support_invite' && !SUPPORT_ROLES.includes(u.role));

    if (loading && users.length === 0) return <AnimatedLoader message="Loading Admin Dashboard..." />;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Super Admin Dashboard
                </Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchUsers}
                        sx={{ mr: 2 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenInviteDialog(true)}
                    >
                        Create Support User
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {/* Dashboard Tabs */}
            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={(e, v) => {
                        setTabValue(v);
                        navigate(`/admin?tab=${v}`, { replace: true });
                    }}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Overview" />
                    <Tab label={`Support Staff (${activeSupportUsers.length})`} />
                    <Tab label={`Pending Invites (${pendingInvites.length})`} />
                    <Tab label={`All Users (${allOtherUsers.length})`} />
                    <Tab label="Audit Logs" />
                    <Tab label="Testing Tools" />
                </Tabs>

                {/* Overview Tab (0) */}
                {tabValue === 0 && (
                    <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                                    <Typography variant="h3" color="primary">{stats.users}</Typography>
                                    <Typography variant="subtitle1" color="textSecondary">Total Users</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fff3e0' }}>
                                    <Typography variant="h3" color="warning.main">{stats.invoices}</Typography>
                                    <Typography variant="subtitle1" color="textSecondary">Invoices Generated</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                                    <Typography variant="h3" color="success.main">{stats.clients}</Typography>
                                    <Typography variant="subtitle1" color="textSecondary">Total Clients</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                                    <Typography variant="h3" color="secondary.main">{stats.inventory}</Typography>
                                    <Typography variant="subtitle1" color="textSecondary">Inventory Items</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#ffebee' }}>
                                    <Typography variant="h3" color="error">{stats.transactions}</Typography>
                                    <Typography variant="subtitle1" color="textSecondary">Total Transactions</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>System Status</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Alert severity="success">Service Region: {awsConfig.region} (Online)</Alert>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Alert severity="info" icon={false}>
                                        API Gateway: {awsConfig.apiGateway.URL ? 'Connected' : 'Disconnected'}
                                    </Alert>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                )}

                {/* Audit Logs Tab (4) */}
                {tabValue === 4 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Admin</TableCell>
                                    <TableCell>Target</TableCell>
                                    <TableCell>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={log.action}
                                                size="small"
                                                color={log.action.includes('DELETE') || log.action.includes('BLOCK') ? 'error' : 'primary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{log.performedBy}</TableCell>
                                        <TableCell>{log.targetUserEmail || 'N/A'}</TableCell>
                                        <TableCell>{log.details}</TableCell>
                                    </TableRow>
                                ))}
                                {logs.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">No audit logs found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Testing Tools Tab (5) */}
                {tabValue === 5 && (
                    <Box sx={{ p: 3 }}>
                        <TestingTools />
                    </Box>
                )}

                {/* User Lists (Tabs 1, 2, 3) */}
                {tabValue > 0 && tabValue < 4 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name / ID</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created/Joined</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(tabValue === 1 ? activeSupportUsers : tabValue === 2 ? pendingInvites : allOtherUsers).map((row) => (
                                    <TableRow key={row.userId || row.uid || row.email}>
                                        <TableCell>{row.displayName || row.userId || 'N/A'}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ROLE_LABELS[row.role as keyof typeof ROLE_LABELS] || row.role}
                                                color={SUPPORT_ROLES.includes(row.role) ? 'primary' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {row.type === 'support_invite' ? (
                                                <Chip label="Invited" color="warning" size="small" variant="outlined" />
                                            ) : (
                                                <Chip label="Active" color="success" size="small" variant="outlined" />
                                            )}
                                        </TableCell>
                                        <TableCell>{new Date(row.createdAt || row.updatedAt).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex' }}>
                                                <IconButton size="small" color="info" onClick={() => handleViewUser(row)} title="View Details">
                                                    <VisibilityIcon />
                                                </IconButton>

                                                {row.type !== 'support_invite' && (
                                                    <IconButton size="small" color="primary" onClick={() => handleEditUser(row)} title="Edit Role/Status">
                                                        <EditIcon />
                                                    </IconButton>
                                                )}

                                                {row.type === 'support_invite' && (
                                                    <IconButton size="small" color="error" onClick={() => handleDeleteInvite(row.email)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(tabValue === 1 ? activeSupportUsers : tabValue === 2 ? pendingInvites : allOtherUsers).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No records found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Edit User Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
                <DialogTitle>Edit User: {selectedUser?.displayName || selectedUser?.email}</DialogTitle>
                <DialogContent sx={{ pt: 2, minWidth: 400 }}>
                    <TextField
                        select
                        margin="dense"
                        label="Role"
                        fullWidth
                        variant="outlined"
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value)}
                        sx={{ mb: 2 }}
                    >
                        {Object.entries(ROLE_LABELS).map(([role, label]) => (
                            <MenuItem key={role} value={role}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Typography variant="subtitle2" gutterBottom>Status</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant={editStatus === 'active' ? 'contained' : 'outlined'}
                            color="success"
                            onClick={() => setEditStatus('active')}
                        >
                            Active
                        </Button>
                        <Button
                            variant={editStatus === 'blocked' ? 'contained' : 'outlined'}
                            color="error"
                            onClick={() => setEditStatus('blocked')}
                        >
                            Blocked
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained" color="primary">Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>User Details</DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Identity</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                        <Typography><strong>ID:</strong> {selectedUser.uid || selectedUser.userId}</Typography>
                                        <Typography><strong>Name:</strong> {selectedUser.displayName || 'N/A'}</Typography>
                                        <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
                                        <Typography><strong>Phone:</strong> {selectedUser.phone || 'N/A'}</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Access & Status</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                        <Typography><strong>Role:</strong> {ROLE_LABELS[selectedUser.role as keyof typeof ROLE_LABELS] || selectedUser.role}</Typography>
                                        <Typography><strong>Status:</strong> {selectedUser.status || 'active'}</Typography>
                                        <Typography><strong>Purpose:</strong> {selectedUser.purpose || 'N/A'}</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Business Details</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                        <Typography><strong>Business Name:</strong> {selectedUser.businessName || 'N/A'}</Typography>
                                        <Typography><strong>GSTN:</strong> {selectedUser.gstn || 'N/A'}</Typography>
                                        <Typography><strong>Address:</strong> {selectedUser.address || 'N/A'}</Typography>
                                        <Typography><strong>City/State:</strong> {selectedUser.city}, {selectedUser.state}</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">System Data</Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                        <pre style={{ margin: 0, overflow: 'auto', maxHeight: 200 }}>{JSON.stringify(selectedUser, null, 2)}</pre>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default SuperAdminDashboard;
